export interface NunchiRule {
  id: number;
  title: string;
  korean: string;
  description: string;
  icon: string;
}

export interface ScenarioOption {
  id: string;
  text: string;
  type: 'HIGH_NUNCHI' | 'LOW_NUNCHI' | 'SELFISH';
}

export interface Scenario {
  id?: string; // Added ID for tracking
  context: string;
  setting: string;
  characters: string;
  options: ScenarioOption[];
  explanation: string; // The "answer key" generated after selection or hidden initially
}

export interface GeneratedScenarioResponse {
  id?: string; // Firestore Doc ID
  scenario: {
    title: string;
    setting: string;
    description: string;
    options: {
      text: string;
      nunchiLevel: 'High' | 'Low' | 'None';
      analysis: string;
    }[];
  }
}

export enum AnalysisState {
  IDLE = 'IDLE',
  ANALYZING = 'ANALYZING',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR'
}

export interface Badge {
  id: string;
  name: string;
  icon: string;
  description: string;
  unlockedAt?: string;
}

export interface UserState {
  uid?: string;
  email?: string;
  name: string;
  isApproved: boolean;
  role?: 'admin' | 'user'; // Added role for RBAC
  joinedAt: string;
  xp: number;
  level: number;
  badges: Badge[];
  scenariosCompleted: number;
  completedScenarioIds: string[]; // NEW: Track specific scenarios seen
  assessmentsCompleted: number;
  history: { date: string; action: string; xpGained: number }[];
}

export interface AssessmentQuestion {
  scenario: string;
  options: {
    text: string;
    score: number; // 0 to 10
  }[];
}

export interface AssessmentResponse {
  questions: AssessmentQuestion[];
}

export interface ZenStory {
  title: string;
  content: string;
  lesson: string;
}

// Ikigai Types
export interface IkigaiPillars {
  love: string;
  goodAt: string;
  worldNeeds: string;
  paidFor: string;
}

export interface IkigaiHistoryEntry {
  id: string;
  timestamp: string;
  pillars: IkigaiPillars;
}

export interface IkigaiSuggestion {
  role: string;
  description: string;
  matchScore: number;
  why: string;
}

export interface IkigaiSynthesisResponse {
  suggestions: IkigaiSuggestion[];
  insight: string;
}

export interface ZenKoan {
  title: string;
  text: string;
  question: string;
}

export interface KoanEvaluation {
  feedback: string;
  masteryLevel: 'Grasshopper' | 'Monk' | 'Master';
}

export interface KoanHistoryEntry {
  id: string;
  timestamp: string;
  koanTitle: string;
  koanText: string;
  question: string;
  userAnswer: string;
  feedback: string;
  masteryLevel: 'Grasshopper' | 'Monk' | 'Master';
}

export interface MindGardenResponse {
  metaphor: string;
  haiku: string;
}

export interface UsageRecord {
  id: string;
  timestamp: string;
  model: string;
  endpoint: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cost: number;
}

// Ichigo Ichie Types
export interface IchigoRule {
  id: number;
  title: string;
  japanese: string;
  description: string;
  icon: string;
}

export interface TeaCeremonyGuide {
  theme: string;
  atmosphere: string;
  steps: string[];
  mindset: string;
}

export interface MomentAnalysis {
  title: string;
  significance: string;
  sensoryDetails: string;
  impermanence: string; // Why this moment will never happen again
}

// Letting Go Types
export interface LettingGoRule {
  id: number;
  title: string;
  concept: string; // e.g. "Non-Attachment"
  description: string;
  icon: string;
}

export interface ReleaseGuidance {
  perspective: string; // A re-framing of the burden
  ritual: string; // A small mental or physical action to release it
  affirmation: string;
}

export interface FlowMeditation {
  title: string;
  visualization: string; // Guided imagery script
  element: string; // Water, Wind, Fire, Earth
}

// Courage Types (Adlerian - Disliked)
export interface AdlerRule {
  id: number;
  title: string;
  concept: string; // e.g. "Separation of Tasks"
  description: string;
  icon: string;
}

export interface TaskSeparationAnalysis {
  yourTask: string;
  theirTask: string;
  advice: string; // How to stop interfering or seeking validation
  mantra: string; // Short phrase to hold boundary
}

export interface TeleologyShift {
  hiddenGoal: string; // The goal behind the emotion (e.g. "You are using anger to control")
  reframe: string; // How to act without that crutch
  action: string; // Courageous step
}

// Happiness Types (Adlerian - Happy)
export interface HappyRule {
  id: number;
  title: string;
  concept: string; // e.g. "From Me to Us"
  description: string;
  icon: string;
}

export interface LoveTaskAnalysis {
  focusShift: string; // Explanation of shifting from "I" to "We"
  diagnosis: string; // Is it transactional (credit) or unconditional (trust)?
  advice: string; // How to love without demanding return
}

export interface SelfRelianceGuide {
  dependencyCheck: string; // Identifying where they seek praise
  mindset: string; // "I have worth because I exist/contribute"
  challenge: string; // A concrete action to prove self-reliance
}

// Kaizen Types
export interface KaizenRule {
  id: number;
  title: string;
  japanese: string;
  description: string;
  icon: string;
}

export interface SmallStepPlan {
  steps: string[]; // List of incredibly small steps
  firstAction: string; // Something they can do in 30 seconds
  motivation: string; // Why small is better than big
}

export interface HanseiReflection {
  acknowledgment: string; // Honest admission of the mistake
  rootCause: string; // The "5 Whys" analysis
  improvement: string; // Concrete plan to prevent recurrence
}

// Stoic Types
export interface StoicRule {
  id: number;
  title: string;
  concept: string; // e.g. "Areté"
  description: string;
  icon: string;
}

export interface DichotomyAnalysis {
  inControl: string[]; // List of things the user controls
  notInControl: string[]; // List of things the user does NOT control
  advice: string; // Stoic advice on where to focus
  mantra: string; // "Some things are up to us, some things are not..."
}

export interface StoicMorningGuide {
  forecast: string; // Anticipated difficulties (Premeditatio Malorum)
  virtues: string[]; // Which virtues to apply (Wisdom, Courage, Justice, Temperance)
  intention: string; // A statement of purpose for the day
}

// Slow Down Types
export interface SlowDownRule {
  id: number;
  title: string;
  chapter: string; // e.g. "Rest"
  description: string;
  icon: string;
}

export interface MindfulPauseResponse {
  perspective: string; // How to see the situation slowly
  action: string; // A calming action (e.g. breathe, walk)
  wisdom: string; // A Haemin Sunim-style quote/thought
}

export interface RelationshipHarmonyGuide {
  empathy: string; // Seeing the other person's struggle
  advice: string; // How to respond with gentleness
  reflection: string; // What this teaches you about yourself
}